/**
 * @file index.tsx
 *
 * @fileoverview Makes some infromation or content collapsible.
 */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDimensions } from "../../../hooks/useDimensions";
import { motion, MotionProps } from "framer-motion";
import styles from "./expander.module.scss";
// Define a new interface that extends MotionProps
type MotionDivProps = MotionProps & React.HTMLAttributes<HTMLDivElement>;
const CustomMotion = motion<MotionDivProps>("div");

// Controls state for the component.
const Context = createContext({
  isOpen: false,
  setIsOpen: () => {},
  controlsId: "epxander",
  showTrigger: false,
  setShowTrigger: (show: boolean) => {},
});

export interface ExpanderProps {
  controlsId?: string;
  children: ReactNode;
  isOpen?: boolean;
  onChange?: () => void;
}

/**
 * Expander is a button that toggles a certain information. It should be used to show or hide certain content when the button is triggered or not. And it
 * follows the https://www.w3.org/TR/wai-aria-practices-1.2/#disclosure ARIA disclosure pattern.
 */
function Expander({
  isOpen,
  onChange,
  controlsId = "epxander",
  children,
}: ExpanderProps) {
  const isControlled = typeof isOpen !== "undefined";
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [internalShowTrigger, setInternalShowTrigger] = useState(true);

  return (
    <Context.Provider
      value={{
        showTrigger: internalShowTrigger,
        setShowTrigger: (show) => setInternalShowTrigger(show),
        controlsId: controlsId,
        isOpen: isControlled ? isOpen : internalIsOpen,
        setIsOpen:
          isControlled && onChange
            ? onChange
            : () => setInternalIsOpen(!internalIsOpen),
      }}
    >
      {children}
    </Context.Provider>
  );
}

/* -------------------------------------------------------------------------------------------------
 * EXPANDER TRIGGER
 * -----------------------------------------------------------------------------------------------*/

export interface ExpanderTriggerProps {
  children: ReactNode;
}
function ExpanderTrigger({ children, ...props }: ExpanderTriggerProps) {
  const context = useContext(Context);
  return context.showTrigger ? (
    <button
      type="button"
      className="jcl-expandr-trigger"
      aria-expanded={context.isOpen}
      aria-controls={context.controlsId}
      {...props}
      onClick={context.setIsOpen}
    >
      {children}
    </button>
  ) : (
    <div />
  );
}

export interface ExpanderContentProps {
  children: ReactNode;
  /**
   * Optionally set a min height fo.
   *
   * @default "0px"
   */
  closedMinHeight?: string;
}

const variants = {
  open: {
    height: "100%",
    transition: { ease: "easeIn", duration: 0.1, staggerChildren: 0.3 },
  },
  closed: (custom) => ({
    height: custom,
    transition: { ease: "easeIn", duration: 0.1, staggerChildren: 0.3 },
  }),
};

/* -------------------------------------------------------------------------------------------------
 * EXPANDER CONTENT
 * -----------------------------------------------------------------------------------------------*/

function ExpanderContent({
  children,
  closedMinHeight = "0px",
}: ExpanderContentProps) {
  const { isOpen, controlsId, setShowTrigger } = useContext(Context);
  const [ref, dimensions] = useDimensions();
  const heightNumber = Number(closedMinHeight.replace("px", ""));

  // Hide the trigger if needed.
  useEffect(() => {
    setShowTrigger(heightNumber < dimensions.height);
  }, [heightNumber, dimensions, setShowTrigger]);

  return heightNumber < dimensions.height ? (
    <div>
      <CustomMotion
        animate={isOpen ? "open" : "closed"}
        initial="closed"
        variants={variants}
        custom={closedMinHeight}
        id={controlsId}
        className={styles.animatedExpanderContent}
      >
        <div ref={ref}>{children}</div>
      </CustomMotion>
    </div>
  ) : (
    <div ref={ref}>{children}</div>
  );
}

export { Expander, ExpanderTrigger, ExpanderContent };
